import { removeSecret } from './secretStorage.js';

// Browser-storage keys that only carry state across a single OAuth redirect
// round-trip (login on LoginPage, account linking on ProfilePage). OAuthPage
// reads them back once and they must not outlive the flow that wrote them.
//
// ENG-319: localStorage persists across sessions, and older builds left
// one-shot keys behind. The StreamerSongList import modal (removed in ENG-290)
// wrote `redirect` + `oauth-provider` and only removed them when the import
// finished cleanly; the Spotify-discontinued path also left `redirect` and
// `state` behind. Pre-fix OAuthPage diverted any callback to a stored
// `redirect` without ever removing it, so every later login was sent to the
// stale page (which exchanged the login code against the wrong endpoint and got
// HTTP 400) instead of `/auth/{provider}/callback` — until the user cleared
// browser storage.

// localStorage keys ProfilePage.handleLinkAccount writes for one linking
// round-trip.
export const LINKING_STATE_KEYS = ['linking-mode', 'linking-provider', 'state'];

// localStorage keys no current code reads but older builds wrote: `redirect`
// (import modal / linking flow), and the plaintext PKCE `verifier` / `challenge`
// from before secrets moved into secretStorage (ENG-256). Cleared so returning
// users' storage converges on the current layout.
export const LEGACY_FLOW_KEYS = ['redirect', 'verifier', 'challenge'];

// localStorage key written by LoginPage.handleLogin.
export const LOGIN_PROVIDER_KEY = 'oauth-provider';

// localStorage key holding the epoch-millisecond moment the browser was handed
// off to a provider, written next to the provider key by LoginPage.handleLogin
// and ProfilePage.handleLinkAccount.
export const FLOW_STARTED_KEY = 'oauth-flow-started';

// How long a hand-off stays answerable. Since ENG-405 the stored provider is
// what tells OAuthPage that a round-trip of this browser's own is in flight,
// and nothing clears it when the user abandons the flow at the provider's
// consent screen — they never come back for the callback that would. Left
// unbounded in localStorage that marker outlives the tab and the browser
// session, so a crafted `/oauth?code=…` link opened days later would still
// find a "login in progress" to attach itself to and could push its own
// authorization code through the exchange. A hand-off the user actually
// completes comes back in seconds; this is wide enough for a consent screen
// that turns into a password reset or a 2FA prompt, and far short of later on.
export const FLOW_MAX_AGE_MS = 30 * 60 * 1000;

// Stamp the moment the browser leaves for the provider.
export function markFlowStarted() {
  try {
    localStorage.setItem(FLOW_STARTED_KEY, String(Date.now()));
  } catch {
    // Unrecorded: the callback finds no hand-off behind it and reports an
    // unfinished sign-in instead of exchanging a code nothing here asked for.
  }
}

// Whether a hand-off recent enough to still be the one this callback answers
// was made from this browser. Missing, unreadable and impossible values (a
// clock moved backwards, a stamp from the future) all count as no hand-off:
// the cost of refusing one is a "sign-in did not finish" the user answers by
// pressing the login button again, and the cost of admitting one is the
// forged-callback exchange above.
export function isFlowInFlight(now = Date.now()) {
  const startedAt = Number(localStorage.getItem(FLOW_STARTED_KEY));
  if (!Number.isFinite(startedAt) || startedAt <= 0) return false;
  const age = now - startedAt;
  return age >= 0 && age <= FLOW_MAX_AGE_MS;
}

// Secrets (sessionStorage via secretStorage) written by
// ProfilePage.handleLinkAccount (`verifier` — only account linking has a PKCE
// pair since ENG-405) and OAuthPage (`code`).
const FLOW_SECRET_KEYS = ['verifier', 'code'];

// The SPA route the provider redirects back to with the one-time `code`.
export const OAUTH_CALLBACK_PATH = '/oauth';

// ENG-406: the callback URL is a single-use URL that browsers nevertheless
// keep — in history, in address-bar autocomplete, in a bookmark, in a restored
// tab. Whoever stores a path to navigate to later (fetchWrapper's
// `post-login-redirect`) must never store this one: by the time it is used its
// code is spent, so replaying it can only end on an error page.
export function isOAuthCallbackPath(path) {
  if (typeof path !== 'string') return false;
  const [pathname] = path.split(/[?#]/);
  // Normalised the way the router matches: react-router resolves routes
  // case-insensitively and tolerates a trailing slash, so `/OAuth` and
  // `/oauth/` render OAuthPage too. Comparing the raw string missed both, and
  // a URL the router treats as the callback would be stored as a page to
  // navigate back to — the bug this guard exists to prevent.
  const normalized = pathname.replace(/\/+$/, '').toLowerCase();
  return (normalized || '/') === OAUTH_CALLBACK_PATH;
}

// ENG-406: fingerprints of the authorization codes this browser has already
// sent to the backend, newest first. A code is single-use, so seeing one twice
// is positive proof that the callback URL carrying it was replayed rather than
// followed — which is what tells a genuine failure (report it) apart from a
// spent code coming back from history (put the user back where they were).
//
// A handful are kept so a Back press further up the stack than the last login
// is still recognised, and the list outlives clearOAuthFlowState: the flow it
// describes is exactly the one that just ended.
const CONSUMED_CODES_KEY = 'oauth-consumed-codes';
const CONSUMED_CODES_KEPT = 5;

// A code is a credential even once spent, so what is stored is a pair of
// 32-bit FNV-1a hashes (of the code and of its reverse) rather than the code:
// enough bits to make a false "already seen" verdict a non-event, and nothing
// that can be read back as the code itself.
export function fingerprintCode(code) {
  if (typeof code !== 'string' || code === '') return null;
  const fnv1a = (input) => {
    let hash = 0x811c9dc5;
    for (let i = 0; i < input.length; i += 1) {
      hash ^= input.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash.toString(36);
  };
  return `${fnv1a(code)}.${fnv1a([...code].reverse().join(''))}`;
}

// Storage can be unavailable (private modes, blocked third-party contexts) or
// full. Either way the only loss is that a replay goes unrecognised, so every
// access falls back to "nothing known" instead of throwing into the callback.
function readConsumedCodes() {
  try {
    const stored = JSON.parse(localStorage.getItem(CONSUMED_CODES_KEY));
    return Array.isArray(stored) ? stored.filter((entry) => typeof entry === 'string') : [];
  } catch {
    return [];
  }
}

export function rememberConsumedCode(code) {
  const fingerprint = fingerprintCode(code);
  if (!fingerprint) return;
  const kept = [fingerprint, ...readConsumedCodes().filter((entry) => entry !== fingerprint)].slice(0, CONSUMED_CODES_KEPT);
  try {
    localStorage.setItem(CONSUMED_CODES_KEY, JSON.stringify(kept));
  } catch {
    // Unrecorded: later replays of this code are treated as first attempts.
  }
}

export function isConsumedCode(code) {
  const fingerprint = fingerprintCode(code);
  return fingerprint !== null && readConsumedCodes().includes(fingerprint);
}

// `error=` value OAuthPage hands /login when a callback arrives with nothing
// behind it and no proof that it is a replay. LoginPage maps it to "sign-in
// did not finish, try again" — the hand-off behind the callback may simply be
// gone (browser storage cleared between leaving and returning, or a consent
// screen sat on for longer than FLOW_MAX_AGE_MS) — and a visitor left on a
// silent login page cannot tell that their sign-in failed rather than never
// started.
export const SIGNIN_INCOMPLETE_ERROR = 'signin-incomplete';

// The other `error=` codes OAuthPage hands /login when a login exchange fails
// (ENG-515). Only these codes travel in the URL, never the server's or the
// provider's own words: those are not localized, and "Failed to exchange
// token: …" says more about the backend than a visitor should read.
export const LOGIN_ERROR = Object.freeze({
  /** The backend could not complete the exchange (5xx, network, a bad or expired session). */
  EXCHANGE_FAILED: 'exchange-failed',
  /** The user declined at the provider's consent screen. */
  ACCESS_DENIED: 'access-denied',
  /** The provider sent back some other error instead of a code. */
  PROVIDER_ERROR: 'provider-error',
  /** The account exists but may not sign in (`login_denied`, ENG-393). */
  ACCOUNT_DISABLED: 'account-disabled',
  /** Any other 401/403 from the exchange. */
  UNAUTHORIZED: 'unauthorized',
});

// The code for a failed login exchange. `providerError` is the callback's
// own `error` parameter, which the provider sets instead of a code when the
// user cancels or something goes wrong on its side; the exchange is still
// made, since the backend answers it, but the provider's report is what
// describes the failure.
export function loginErrorCode(error, providerError) {
  if (providerError) {
    return providerError === 'access_denied' ? LOGIN_ERROR.ACCESS_DENIED : LOGIN_ERROR.PROVIDER_ERROR;
  }
  if (error?.code === 'login_denied') return LOGIN_ERROR.ACCOUNT_DISABLED;
  if (error?.status === 401 || error?.status === 403) return LOGIN_ERROR.UNAUTHORIZED;
  return LOGIN_ERROR.EXCHANGE_FAILED;
}

// Same for an account-linking round-trip whose state is gone: ProfilePage
// words any error it does not recognise as "we could not link that account".
export const LINKING_INCOMPLETE_MESSAGE = 'Account linking did not finish';

// Where a browser that reaches the callback with no flow state behind it
// belongs (ENG-406). There is no code to exchange, so the visit says nothing
// about the session this browser already has: a signed-in user goes back into
// the app — to /profile if the URL is from an account-linking round-trip,
// whose `state` ProfilePage.handleLinkAccount mints as `link:{userId}:{random}`
// — and only a signed-out one is sent to /login.
//
// `knownReplay` says the code was already exchanged by this browser, so
// nothing has gone wrong and there is nothing to tell the user. Without it all
// that is known is that no hand-off stands behind the callback, which a
// stored provider that was cleared or has aged out produces as readily as a
// replay, so the arrival is reported where the user would otherwise be left
// guessing.
export function replayedCallbackDestination({ isSignedIn, state, knownReplay = false }) {
  const isLinking = typeof state === 'string' && state.startsWith('link:');
  if (isSignedIn) {
    if (isLinking && !knownReplay) {
      return `/profile?linked=error&error=${encodeURIComponent(LINKING_INCOMPLETE_MESSAGE)}`;
    }
    return isLinking ? '/profile' : '/home';
  }
  return knownReplay ? '/login' : `/login?error=${SIGNIN_INCOMPLETE_ERROR}`;
}

// Drop the localStorage state of any earlier login/linking attempt without
// touching secrets. LoginPage and ProfilePage both call this as a hand-off
// begins, immediately before recording the flow of their own: at most one
// round-trip is ever in flight, so the keys of any earlier one are leftovers.
//
// Secrets are left alone because sessionStorage is this tab's storage and the
// verifier in it may belong to a linking attempt running in this very tab
// under an older mount — not because the linking attempt is being preserved.
// It is not: LINKING_STATE_KEYS goes with everything else, so a verifier that
// survives here is orphaned, and clearOAuthFlowState (the end of a round-trip,
// where the two can be cleared together) is what removes it.
export function clearStaleFlowStorage() {
  [...LINKING_STATE_KEYS, ...LEGACY_FLOW_KEYS, LOGIN_PROVIDER_KEY, FLOW_STARTED_KEY]
    .forEach(key => localStorage.removeItem(key));
}

// Reset every login/linking key, secrets included, once a round-trip is over
// (callback succeeded, failed, or was rejected) so the next flow starts clean.
export function clearOAuthFlowState() {
  clearStaleFlowStorage();
  FLOW_SECRET_KEYS.forEach(key => removeSecret(key));
}
